// VpnList.tsx
import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Checkbox, Divider, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, GridColDef, type GridRenderCellParams, type GridRowIdGetter } from '@mui/x-data-grid';
import { useFetch } from '../../hooks/useFetch';
import { usePresetDialogs } from '../dialog';
import { FormModal } from '../modal/FormModal';
import ActionButtons from '../ActionButtons';
import { useTranslation } from 'react-i18next';
import { Delete, Edit, } from '@mui/icons-material';
import { api } from '../../api/data/vpnApi';
import { useCustomer } from '../../context/CustomerContext';
import VpnForm from './VpnForm';
import { WireguardConf } from './vpn';
import { useApiAction } from '../../hooks/useApiAction2';

export const VpnList: React.FC = () => {
    const { t } = useTranslation();
    const { data, setData, error } = useFetch<WireguardConf[]>(api.getWireguardConf, []);
    const { customerData, isLoading: isCustomerLoading } = useCustomer(); // Access customer context
    const { showInfo, showConfirm, showPrompt, showOptions, DialogComponent } = usePresetDialogs();
    const [selectedVPn, setSelectedVpn] = useState<WireguardConf | null>(null);    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const { upsert, remove, isActing, errorMessage } = useApiAction({
        setData,
        pk: 'id', 
        defaultConfirmFn: showConfirm,
    });

    if (!customerData?.customer) {
        return <Typography>t('customer.loading')</Typography>;
    }    

    const columns: GridColDef[] = [
        { field: "id", headerName: 'id', type: "number", width: 70, },
        { field: "name", headerName: t("VpnList.name_Label"), type: "string", flex: 2, width: 100, display: 'flex', }, 
        { field: "conf", headerName: t("VpnList.conf_Label"), type: "string", flex: 2, width: 100, display: 'flex', }, 
        { field: "comment", headerName: t("VpnList.comment_Label"), type: "string", flex: 2, width: 100, display: 'flex', }, 
        { field: 'enabled',
            headerName: t('VpnList.enabled'),
            width: 120,
            renderCell: (params) => {
                let backgroundColor;
                if (params.value === true) {
                    backgroundColor = 'green';
                } else if (params.value === false) {
                    backgroundColor = 'red';
                }
                return (
                    <Box
                    sx={{
                        mt: 2,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: backgroundColor,
                    }}
                    />
                );
            },      
        },        
        {
            field: 'actions',
            headerName: t('common.actions'),
            width: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams<WireguardConf>) => {
                return (
                    <ActionButtons actions={[
                        {type: 'edit', action: () => handleOpenModal(params.row), },
                        {type: 'delete', action: () => handleDelete(params.row.id || -1), },
                    ]} />
                );
            },
        },        
    ];

    // Сохранение (создание / обновление)
    const handleUpdate = async (formData: WireguardConf) => {
        await upsert({
            data: formData,
            updateAction: api.putWireguardConf,
            createAction: api.postWireguardConf,
            onSuccess: handleCloseModal,
            errorMessage: t('VpnList.save_error'),
        });
    };

    // const handleUpdate = async (data: WireguardConf) => {
    //     if (!!selectedVPn) {
    //         await update (
    //             () => api.putWireguardConf(data), 
    //             () => setData((prev) => prev.map(rtc => rtc.id === data.id ? data : rtc))
    //         );
    //     } else {
    //         await create (
    //             async () => {
    //                 const newData = await api.postWireguardConf(data);
    //                 setData((prev) => [...prev, newData]);
    //             }
    //         );
    //     }
    //     handleCloseModal();
    // };

    // const handleUpdate = async (data: WireguardConf) => {
    //     if (!!selectedVPn) {
    //         await api.putWireguardConf(data);
    //         setData((prev) => prev.map(rtc => rtc.id === data.id ? data : rtc));
    //     } else {
    //         const newData = await api.postWireguardConf(data);
    //         setData((prev) => [...prev, newData]);
    //     }
    //     handleCloseModal();
    // };

    const handleDelete = async (id: number) => {
        await remove({
            id,
            action: api.deleteWireguardConf,
            confirm: {
                title: t('common.delete_confirmation'),
                message: `${t('VpnList.Are_you_sure_you_want_to_delete_vpn')} ${id}?`,
            },
            errorMessage: t('VpnList.delete_error'),
        });
    };

    // const handleDelete = async (id: number) => {
    //     await remove(
    //         () => api.deleteWireguardConf(id),
    //         {
    //             title: t('common.delete_confirmation'),
    //             message: `${t('VpnList.Are_you_sure_you_want_to_delete_vpn')} ${id}?`,
    //         },
    //         () => {
    //             setData(prev => prev.filter(rtc => id !== rtc.id));
    //             console.log(`WireguardConf with ID ${id} deleted.`);
    //         },
    //         t('VpnList.delete_error')
    //     );
    // };

    // const handleDelete = async (id: number) => {
    //     const result = await showConfirm(
    //         t('VpnList.Are_you_sure_you_want_to_delete_vpn') + ' ' + id + '?', 
    //         t('common.delete_confirmation'));

    //     if (!result) return;

    //     await api.deleteWireguardConf(id);

    //     const updated = data.filter(rtc => id !== rtc.id);
    //     setData(updated);
    //     console.log(`WireguardConf with ID ${id} deleted.`);
    // };

    const handleOpenModal = (wc: WireguardConf | null) => {
        setSelectedVpn(wc);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);

// --- Рендер карточки для мобильных устройств ---
    const renderMobileCard = (wc: WireguardConf) => (
        <Card key={wc.id} sx={{ mb: 2, boxShadow: 2 }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    {/* <Typography variant="h6" color="primary">
                        {wc.name}
                    </Typography> */}
                    <Box>
                        <IconButton onClick={() => handleOpenModal(wc)} size="small"><Edit /></IconButton>
                        <IconButton onClick={() => handleDelete(wc.id || -1)} size="small" color="error"><Delete /></IconButton>
                    </Box>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
            </CardContent>
        </Card>
    );
    
    return (
  
        <Box sx={{ height: 400, width: '100%', p: isMobile ? 1 : 0, ml: '20px', mt: '20px',  }}>
            
            {DialogComponent} {/* for 'confirm' dialog*/}
                 
            <Typography variant="h5" gutterBottom>
                Налаштування VPN
            </Typography>
            
            <Stack 
                direction={isMobile ? "column" : "row"} 
                spacing={2} 
                sx={{ mb: 2 }} 
                alignItems="center"
            >
                <Button 
                    fullWidth={isMobile}
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenModal(null)}
                >
                    {t('VpnList.Add_New_Configuration')}
                </Button>
            </Stack>

            {/* Основной контент: Сетка или Карточки */}
            {isMobile ? (
                <Box sx={{ pb: 2 }}>
                    {data.map(plate => renderMobileCard(plate))}
                </Box>
            ) : (
                <DataGrid
                    // lang={i18n.language as any}
                    // gridId='ReactionsList'
                    // hideToolbar={true}
                    rows={data}
                    columns={columns}
                    getRowId={(row) => row.id}
                    initialState={{
                        columns: {columnVisibilityModel: {id: false}}
                    }}
                />
            )}

            <Typography color='error'>{error?.message}</Typography>

            {errorMessage && (
                <Typography color="error">
                    {errorMessage}
                </Typography>
            )}

            <FormModal open={isModalOpen} onClose={handleCloseModal}>
                <VpnForm
                    wc={selectedVPn}
                    onSave={handleUpdate}
                    onCancel={handleCloseModal}
                />
            </FormModal>
        </Box>
  
    );
};